import React, { useEffect, useRef, useState, useCallback } from 'react';
import type { StatsComponent as StatsComponentType } from '../schemas/validation';
import { useThemeSafe } from '../contexts/ThemeContext';
import { useCustomColors, useBrandFonts } from '../contexts/CustomColorsContext';
import { getThemeStyles } from '../styles/themes';
import { renderTextWithLineBreaks } from '../utils/textFormatting';
import { getFontFamily } from '../utils/fontLoader';

interface StatsComponentProps {
  component: StatsComponentType;
}

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

function formatNumber(n: number, isDecimal: boolean): string {
  if (isDecimal) return n.toFixed(1);
  return Math.round(n).toLocaleString();
}

export const StatsComponent: React.FC<StatsComponentProps> = ({ component }) => {
  const { data } = component;
  const { currentTheme } = useThemeSafe();
  const customColors = useCustomColors();
  const brandFonts = useBrandFonts();
  const themeStyles = getThemeStyles(currentTheme, customColors || undefined);

  const headingFontStyle = brandFonts?.headingFont
    ? { fontFamily: getFontFamily(brandFonts.headingFont) }
    : {};
  const bodyFontStyle = brandFonts?.bodyFont
    ? { fontFamily: getFontFamily(brandFonts.bodyFont) }
    : {};

  const backgroundColor =
    (data as any).customColors?.background || themeStyles.stats?.background || '#ffffff';
  const titleColor =
    (data as any).customColors?.title || themeStyles.stats?.titleColor || '#111827';
  const subtitleColor =
    (data as any).customColors?.subtitle || themeStyles.stats?.subtitleColor || '#6b7280';
  const valueColor =
    (data as any).customColors?.value || themeStyles.stats?.valueColor || '#3b82f6';
  const labelColor =
    (data as any).customColors?.label || themeStyles.stats?.labelColor || '#111827';
  const descriptionColor =
    (data as any).customColors?.description || themeStyles.stats?.descriptionColor || '#6b7280';

  const items = data.items || [];
  const layout = data.layout || 'row';
  const columns = data.columns || 3;
  const animated = data.animated ?? true;
  const animationDuration = data.animationDuration || 2000;

  const sectionRef = useRef<HTMLElement>(null);
  const [hasAnimated, setHasAnimated] = useState(false);

  // Treat "New Stat" with value 0 as unfilled - don't show the number
  const isUnfilledPlaceholder = (item: (typeof items)[number]) =>
    item.value === 0 && item.label === 'New Stat';

  const [displayValues, setDisplayValues] = useState<string[]>(
    items.map(item => {
      if (isUnfilledPlaceholder(item)) return '';
      const prefix = item.prefix || '';
      const suffix = item.suffix || '';
      return animated
        ? `${prefix}0${suffix}`
        : `${prefix}${formatNumber(item.value, item.value % 1 !== 0)}${suffix}`;
    })
  );

  // Sync display values when items change so edits appear immediately in the editor
  useEffect(() => {
    setDisplayValues(
      items.map(item => {
        if (isUnfilledPlaceholder(item)) return '';
        const prefix = item.prefix || '';
        const suffix = item.suffix || '';
        return `${prefix}${formatNumber(item.value, item.value % 1 !== 0)}${suffix}`;
      })
    );
  }, [items]);

  const animate = useCallback(() => {
    if (hasAnimated || !animated) return;
    setHasAnimated(true);

    const startTime = performance.now();

    function tick(now: number) {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / animationDuration, 1);
      const easedProgress = easeOutCubic(progress);

      const newValues = items.map(item => {
        if (isUnfilledPlaceholder(item)) return '';
        const prefix = item.prefix || '';
        const suffix = item.suffix || '';
        const current = easedProgress * item.value;
        return `${prefix}${formatNumber(current, item.value % 1 !== 0)}${suffix}`;
      });

      setDisplayValues(newValues);

      if (progress < 1) {
        requestAnimationFrame(tick);
      }
    }

    requestAnimationFrame(tick);
  }, [hasAnimated, animated, animationDuration, items]);

  useEffect(() => {
    if (!animated) {
      setDisplayValues(
        items.map(item => {
          if (isUnfilledPlaceholder(item)) return '';
          const prefix = item.prefix || '';
          const suffix = item.suffix || '';
          return `${prefix}${formatNumber(item.value, item.value % 1 !== 0)}${suffix}`;
        })
      );
      return;
    }

    const node = sectionRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) {
          animate();
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [animated, animate, items]);

  // Tailwind can't generate dynamic classes - use explicit mapping (columns is 2-4 per schema).
  // Third+ columns only at lg+ so phones and small tablets stack instead of squeezing side-by-side.
  const gridColsMap: Record<number, string> = {
    2: 'grid grid-cols-1 md:grid-cols-2 gap-8 [&>*]:min-w-0',
    3: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 [&>*]:min-w-0',
    4: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 [&>*]:min-w-0',
  };
  const gridClasses =
    layout === 'grid'
      ? (gridColsMap[columns] ?? gridColsMap[3])
      : 'flex flex-wrap items-start justify-center gap-12';

  return (
    <section ref={sectionRef} className="py-16 md:py-20" style={{ backgroundColor }}>
      <div className="max-w-7xl mx-auto min-w-0 px-4 sm:px-6 lg:px-8">
        {(data.title || data.subtitle) && (
          <div className="text-center mb-12">
            {data.title && (
              <h2
                className="text-2xl md:text-3xl font-bold mb-2"
                style={{ color: titleColor, ...headingFontStyle }}
                dangerouslySetInnerHTML={{ __html: renderTextWithLineBreaks(data.title) }}
              />
            )}
            {data.subtitle && (
              <p
                className="text-base opacity-80"
                style={{ color: subtitleColor, ...bodyFontStyle }}
                dangerouslySetInnerHTML={{ __html: renderTextWithLineBreaks(data.subtitle) }}
              />
            )}
          </div>
        )}

        <div className={gridClasses}>
          {items.map((item, index) => (
            <div key={index} className="text-center">
              <div
                className="text-4xl md:text-5xl font-bold mb-2"
                style={{ color: valueColor, ...headingFontStyle }}
              >
                {displayValues[index] ?? ''}
              </div>
              <div
                className="text-base font-medium"
                style={{ color: labelColor, ...bodyFontStyle }}
              >
                {item.label}
              </div>
              {item.description && (
                <p className="text-sm mt-1" style={{ color: descriptionColor, ...bodyFontStyle }}>
                  {item.description}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
